import { Directive, effect, ElementRef, HostListener, inject, input, Renderer2 } from '@angular/core';
import { CollectionSelectionService } from './collection-selection.service';

interface DescendantState {
  ariaDisabled: string | null;
  disabled: boolean | undefined;
  pointerEvents: string;
  tabIndex: string | null;
}

type DisableableElement = HTMLElement & { disabled: boolean };

const FOCUSABLE_SELECTOR = 'a[href], area[href], button, input, select, textarea, summary, [tabindex], [contenteditable="true"]';

@Directive({
  selector: '[zagSelectable]',
  standalone: true,
  host: {
    '[attr.zagSelectable]': 'entityId()',
    '[attr.tabindex]': 'selectionMode() ? "0" : null',
    '[attr.role]': 'selectionMode() ? "checkbox" : null',
    '[attr.aria-checked]': 'selectionMode() ? isSelected() : null',
    '[class.zag-selectable]': 'selectionMode()',
    '[class.zag-selectable--selected]': 'selectionMode() && isSelected()',
  },
})
export class SelectableDirective {
  private readonly el = inject(ElementRef<HTMLElement>);
  private readonly renderer = inject(Renderer2);
  private readonly selectionService = inject(CollectionSelectionService, { optional: true });
  private overlay: HTMLElement | null = null;
  private readonly descendantStates = new Map<HTMLElement, DescendantState>();
  private descendantObserver: MutationObserver | null = null;

  readonly entityId = input.required<string>({ alias: 'zagSelectable' });
  readonly selectionMode = input<boolean>(false);

  constructor() {
    effect(onCleanup => {
      if (!this.selectionMode()) {
        this.restoreDescendants();
        this.stopObservingDescendants();
        return;
      }

      this.ensureCheckboxOverlay();
      this.suppressDescendants();
      this.observeDescendants();
      this.focusHostIfDescendantHasFocus();
      onCleanup(() => {
        this.restoreDescendants();
        this.stopObservingDescendants();
      });
    });
  }

  protected isSelected(): boolean {
    return this.selectionService?.isSelected(this.entityId()) ?? false;
  }

  @HostListener('click', ['$event'])
  onClick(event: MouseEvent): void {
    if (!this.selectionMode() || !this.selectionService) return;
    event.stopPropagation();
    this.selectionService.toggle(this.entityId());
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.selectionMode() || !this.selectionService) return;
    if (event.key === ' ' || event.key === 'Enter') {
      event.preventDefault();
      this.selectionService.toggle(this.entityId());
    }
  }

  @HostListener('focus')
  onFocus(): void {
    if (this.selectionMode() && typeof this.el.nativeElement.scrollIntoView === 'function') {
      this.el.nativeElement.scrollIntoView({ block: 'nearest' });
    }
  }

  private ensureCheckboxOverlay(): void {
    if (this.overlay) return;
    const overlay = this.renderer.createElement('span') as HTMLElement;
    this.renderer.addClass(overlay, 'zag-selectable__checkbox-overlay');
    this.renderer.setAttribute(overlay, 'aria-hidden', 'true');
    this.renderer.appendChild(this.el.nativeElement, overlay);
    this.overlay = overlay;
  }

  private suppressDescendants(): void {
    const host = this.el.nativeElement as HTMLElement;
    host.querySelectorAll<HTMLElement>('*').forEach(element => this.suppressElement(element));
  }

  private suppressElement(element: HTMLElement): void {
    if (element === this.overlay || this.descendantStates.has(element)) return;

    const disableable = isDisableableElement(element);
    this.descendantStates.set(element, {
      ariaDisabled: element.getAttribute('aria-disabled'),
      disabled: disableable ? element.disabled : undefined,
      pointerEvents: element.style.pointerEvents,
      tabIndex: element.getAttribute('tabindex'),
    });

    this.renderer.setStyle(element, 'pointer-events', 'none');
    if (element.matches(FOCUSABLE_SELECTOR)) {
      this.renderer.setAttribute(element, 'tabindex', '-1');
      this.renderer.setAttribute(element, 'aria-disabled', 'true');
    }
    if (disableable) this.renderer.setProperty(element, 'disabled', true);
  }

  private restoreDescendants(): void {
    for (const [element, state] of this.descendantStates) {
      if (state.pointerEvents) {
        this.renderer.setStyle(element, 'pointer-events', state.pointerEvents);
      } else {
        this.renderer.removeStyle(element, 'pointer-events');
      }

      if (state.tabIndex === null) {
        this.renderer.removeAttribute(element, 'tabindex');
      } else {
        this.renderer.setAttribute(element, 'tabindex', state.tabIndex);
      }

      if (state.ariaDisabled === null) {
        this.renderer.removeAttribute(element, 'aria-disabled');
      } else {
        this.renderer.setAttribute(element, 'aria-disabled', state.ariaDisabled);
      }

      if (isDisableableElement(element) && state.disabled !== undefined) {
        this.renderer.setProperty(element, 'disabled', state.disabled);
      }
    }
    this.descendantStates.clear();
  }

  private observeDescendants(): void {
    if (this.descendantObserver || typeof MutationObserver === 'undefined') return;

    this.descendantObserver = new MutationObserver(mutations => {
      if (!this.selectionMode()) return;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== Node.ELEMENT_NODE) continue;
          const element = node as HTMLElement;
          this.suppressElement(element);
          element.querySelectorAll<HTMLElement>('*').forEach(child => this.suppressElement(child));
        }
      }
    });
    this.descendantObserver.observe(this.el.nativeElement, { childList: true, subtree: true });
  }

  private stopObservingDescendants(): void {
    this.descendantObserver?.disconnect();
    this.descendantObserver = null;
  }

  private focusHostIfDescendantHasFocus(): void {
    const activeElement = this.el.nativeElement.ownerDocument?.activeElement;
    if (activeElement && activeElement !== this.el.nativeElement && this.el.nativeElement.contains(activeElement)) {
      this.el.nativeElement.focus();
    }
  }
}

function isDisableableElement(element: HTMLElement): element is DisableableElement {
  return 'disabled' in element;
}
